import styled from "@emotion/styled";
import { Button, CircularProgress, Alert } from "@mui/material";
import { useAppContext } from "../../../../AppContext";
import { observer } from "mobx-react";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {SubscriptionPlanDto, SubscriptionState} from "../../../../apis/PaymentApi";
import { SettingStep } from "../../../../stores/SettingsStore";

export type PaymentProps = {
  shortToken?: string | undefined;
  selectedPlanId?: string | undefined;
};

const TRIAL_FEATURES = [
  {
    title: "Получай разбор после каждой тренировки",
    subtitle: "Что получилось и что делать дальше",
  },
  {
    title: "Ни одного вопроса без ответа",
    subtitle: "Что с формой, почему пульс вырос, что есть перед стартом",
  },
  {
    title: "Поручи бро вести календарь",
    subtitle: "Внести план, поправить, добавить, удалить",
  },
];

export const Payment = observer((props: PaymentProps) => {
  const {
    store,
    api: { paymentApi, mobileUserApi },
  } = useAppContext();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] =
    useState<SubscriptionPlanDto[]>([]);
  const [paymentResult, setPaymentResult] = useState<{
    status: string;
    orderId: string;
    nextBillingDate?: string;
    recurringAmount?: number;
    preAuthAmount?: number;
  } | null>(null);

  const checkPaymentResult = useCallback(
    async (orderId: string, bindingId: string) => {
      try {
        setIsLoading(true);
        setError(null);

        // Обрабатываем успешное создание привязки
        await paymentApi.handleBindingSuccess(orderId, bindingId, props.shortToken ?? null);

        // Обновляем стор подписок, чтобы UI сразу увидел активную подписку
        await store.subscriptionPlanStore.loadSubscriptionPlans(props.shortToken ?? null);

        const activeBillings =
          store.subscriptionPlanStore.myBillings?.filter((b) => b.isActive) ?? [];

        const activePlans = activeBillings
          .map((b) => b.subscriptionPlan)
          .filter((p): p is SubscriptionPlanDto => p != null);
        if (activePlans.length > 0) {
          setSubscriptionPlans(activePlans);
        }

        const nextBillingDate =
          activeBillings
            .map((b) => b.nextPaymentDate)
            .filter(Boolean)
            .map((d) => new Date(d))
            .sort((a, b) => a.getTime() - b.getTime())[0] ??
          (() => {
            const d = new Date();
            d.setMonth(d.getMonth() + 1);
            return d;
          })();

        const activePlan = store.subscriptionPlanStore.getActivePlans()[0];
        if (activePlan) {
          const endDateFormatted = activePlan.endDate
            ? new Date(activePlan.endDate).toLocaleDateString("ru-RU")
            : undefined;
          store.settingsStore.setYnaProCurrentActive(true);
          store.settingsStore.setYnaProActivePlan(
            activePlan.id,
            activePlan.name,
            "success",
            activePlan.price,
            endDateFormatted
          );

          store.settingsStore.setStepType(SettingStep.YnaProActive);
        }

        setPaymentResult({
          status: "Deposited",
          orderId: orderId,
          nextBillingDate: nextBillingDate.toLocaleDateString("ru-RU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }),
        });
      } catch (err: any) {
        console.error("Ошибка при проверке статуса платежа:", err);
        setError("Ошибка при проверке статуса платежа");
      } finally {
        setIsLoading(false);
      }
    },
    [paymentApi, store, props.shortToken]
  );

  // Проверяем URL параметры для отображения результата платежа
  useEffect(() => {
    const orderId = searchParams.get("orderId");
    const bindingId = searchParams.get("bindingId");
    if (orderId && bindingId) {
      checkPaymentResult(orderId, bindingId);
    }
  }, [searchParams, checkPaymentResult]);

  // Загружаем планы подписки при открытии
  useEffect(() => {
    const loadSubscriptionPlans = async () => {
      try {
        setIsLoading(true);

        if (!store.subscriptionPlanStore.myBillings) {
          await store.subscriptionPlanStore.loadSubscriptionPlans(props.shortToken ?? null);
        }

        const myBillings = await paymentApi.getMyBilling(props.shortToken);

        const billings = Array.isArray(myBillings) ? myBillings : [myBillings];
        const activeSubscriptionPlans = billings
          .filter(i => i.isActive && i.subscriptionPlan != null)
          .map(i => {
            const result = i.subscriptionPlan as SubscriptionPlanDto;
            result.state = i.state;
            return result;
          });
        
        setSubscriptionPlans(activeSubscriptionPlans);
      } catch (err: any) {
        console.error("Ошибка при загрузке планов подписки:", err);
        setError("Не удалось загрузить информацию о подписке");
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscriptionPlans();
  }, [paymentApi, props.shortToken]);

  const handleActivateSubscription = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (props.shortToken && !store.authStore.currentUser?.id) {
        await mobileUserApi.getUserInfo(props.shortToken);
      }

      const selectedPlanId =
        store.settingsStore.selectedSubscriptionPlanId || props.selectedPlanId || undefined;

      // Создаем заказ на привязку карты с рекуррентным платежом
      const bindingData = {
        amount: 1, // Минимальная сумма для создания привязки (1 рубль)
        description: "YNA Bro - Рекуррентная подписка",
        clientId: store.authStore.currentUser?.id || "",
        recurringFrequency: "Monthly", // Ежемесячная подписка
        recurringExpiry: new Date(
          new Date().setFullYear(new Date().getFullYear() + 1)
        ).toISOString(), // Подписка на 1 год
        forceCreateBinding: true, // Принудительное создание привязки
        subscriptionPlanId: selectedPlanId,
        fromMobile: props.shortToken && props.shortToken !== '' ? true : false
      };

      console.log("Создание заказа на привязку с данными:", bindingData);

      const response = await paymentApi.createBindingOrder(bindingData, props.shortToken);

      console.log("Ответ от API:", response);

      if (response.errorCode) {
        setError(
          response.errorMessage || "Ошибка при создании заказа на привязку"
        );
        return;
      }

      // Перенаправляем на страницу оплаты для привязки карты
      if (response.paymentUrl) {
        window.location.href = response.paymentUrl;
      } else {
        setError("Не получен URL для оплаты");
      }
    } catch (err: any) {
      console.error("Ошибка при активации подписки:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Произошла ошибка при создании заказа. Попробуйте еще раз."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const selectedPlanId = store.settingsStore.selectedSubscriptionPlanId || props.selectedPlanId;
  const subscriptionPlan = store.subscriptionPlanStore.subscriptionPlans.find(i => i.id === selectedPlanId);

  const mobilePadding = () => {
    return props.shortToken ? {padding: '60px'} : undefined;
  }
  
  const isCancelled = () => {
    const selectedPlanId =
      store.settingsStore.selectedSubscriptionPlanId || props.selectedPlanId || undefined;
    
    const plan = store.subscriptionPlanStore.myBillings?.find((b) => b.subscriptionPlan?.id == selectedPlanId);

    return plan?.isActive && plan?.state === SubscriptionState.Cancelled;
  }

  const TrialContent = () => <>
      <SHeroTitle>
        Всё, что умеет бро,
        <br />
        без ограничений
      </SHeroTitle>

      {error && (
        <SAlert severity="error" onClose={() => setError(null)}>
          {error}
        </SAlert>
      )}

      {connectionStatus && (
        <SAlert
          severity="success"
          onClose={() => setConnectionStatus(null)}
        >
          {connectionStatus}
        </SAlert>
      )}

      <SFeatureList>
        {TRIAL_FEATURES.map((feature) => (
          <SFeatureItem key={feature.title}>
            <SFeatureTitle>{feature.title}</SFeatureTitle>
            <SFeatureSubtitle>{feature.subtitle}</SFeatureSubtitle>
          </SFeatureItem>
        ))}
      </SFeatureList>

      <SButtonSection>
        <SActivateButton
          onClick={handleActivateSubscription}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" />
              <SLoadingText>Создание заказа...</SLoadingText>
            </>
          ) : (
            "Активировать бесплатно на 14 дней"
          )}
        </SActivateButton>
      </SButtonSection>

      <SFooterText>
        Затем {subscriptionPlan?.price} ₽ в месяц. Отменить можно в любой момент
      </SFooterText>
    </>

  const ReactivateContent = () => <>
      <SHeroTitle>
        Всё, что умеет бро,
        <br />
        без ограничений
      </SHeroTitle>

      {error && (
        <SAlert severity="error" onClose={() => setError(null)}>
          {error}
        </SAlert>
      )}

      {connectionStatus && (
        <SAlert
          severity="success"
          onClose={() => setConnectionStatus(null)}
        >
          {connectionStatus}
        </SAlert>
      )}

      <SFeatureList>
        {TRIAL_FEATURES.map((feature) => (
          <SFeatureItem key={feature.title}>
            <SFeatureTitle>{feature.title}</SFeatureTitle>
            <SFeatureSubtitle>{feature.subtitle}</SFeatureSubtitle>
          </SFeatureItem>
        ))}
      </SFeatureList>

      <SButtonSection>
        <SActivateButton
          onClick={handleActivateSubscription}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" />
              <SLoadingText>Создание заказа...</SLoadingText>
            </>
          ) : (
            "Активировать подписку"
          )}
        </SActivateButton>
      </SButtonSection>

      <SFooterText>
        Спишется {subscriptionPlan?.price} ₽ в месяц. Отменить можно в любой момент
      </SFooterText>
    </>

  return (
    <SContent style={mobilePadding()}>
      {paymentResult ? (
        <SPaymentSuccessContent>
        </SPaymentSuccessContent>
      ) : isCancelled() ? ReactivateContent() : TrialContent()}
    </SContent>
  );
});

const SContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
`;

const SHeroTitle = styled.div`
  font-family: "Golos Text", sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 1.3;
  color: #1a222d;
  text-align: center;
  width: 100%;
`;

const SFeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
`;

const SFeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
`;

const SFeatureTitle = styled.div`
  font-family: "Golos Text", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.4;
  color: #1a222d;
`;

const SFeatureSubtitle = styled.div`
  font-family: "Golos Text", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  color: #7b8a9e;
`;

const SFooterText = styled.div`
  font-family: "Golos Text", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  color: #7b8a9e;
  text-align: center;
  width: 100%;
`;

const SButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  width: 100%;
`;

const SActivateButton = styled(Button)`
  background: #ff3147;
  border-radius: 12px;
  padding: 16px 24px;
  width: 100%;
  font-family: "Golos Text", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.3;
  color: white;
  text-transform: none;
  white-space: nowrap;
  cursor: pointer;
  border: none;
  min-width: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #e02a3e;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const SAlert = styled(Alert)`
  margin-bottom: 16px;
  width: 100%;
`;

const SLoadingText = styled.span`
  margin-left: 8px;
`;

const SPaymentSuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
`;

const SSuccessHeader = styled.div`
  box-sizing: border-box;
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  padding: 40px 0 20px;
  position: relative;
  width: 100%;
`;

const SSuccessTitle = styled.div`
  font-family: "Golos Text", sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 1.3;
  color: #1a222d;
  text-align: center;
  width: 100%;
`;

const SSuccessInfo = styled.div`
  background: white;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: flex-start;
  padding: 20px 0;
  position: relative;
  width: 100%;
  border-radius: 8px;
`;

const SSuccessInfoText = styled.div`
  font-family: "Golos Text", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.47;
  color: #1a222d;
  text-align: center;
  width: 100%;
  margin-bottom: 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SCancelSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  width: 100%;
`;

const SCancelButton = styled(Button)`
  background: #eef5fb;
  border-radius: 6px;
  padding: 12px 16px;
  font-family: "Golos Text", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.3;
  color: #1a222d;
  text-transform: none;
  white-space: nowrap;
  cursor: pointer;
  border: none;
  min-width: auto;

  &:hover {
    background: #ddeaf7;
  }
`;

const SCancelDisclaimer = styled.div`
  background: white;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: flex-start;
  padding: 20px 0;
  position: relative;
  width: 100%;
  border-radius: 8px;
`;

